import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Task, TaskCategory, TaskStatus, Priority } from '../types';

// ─── Date helpers ─────────────────────────────────────────────
function toYMD(d: Date) {
  return d.toISOString().slice(0, 10);
}

function getWeekBounds(anchor: Date) {
  const d   = new Date(anchor);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const start = new Date(d);
  start.setDate(diff);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

function getMonthBounds(anchor: Date) {
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end   = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  return { start, end };
}

function dayLabel(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function pct(done: number, total: number) {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

function fmtPeriod(start: Date, end: Date) {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

// ─── CSV export ───────────────────────────────────────────────
function exportCSV(tasks: Task[], filename: string) {
  const header = ['Title', 'Category', 'Priority', 'Status', 'Task Date', 'Deadline', 'Carried Over', 'Progress (%)'];
  const rows = tasks.map(t => [
    `"${t.title.replace(/"/g, '""')}"`,
    t.category,
    t.priority,
    t.status,
    t.task_date,
    t.deadline ? new Date(t.deadline).toLocaleString() : '',
    t.carried_over ? 'Yes' : 'No',
    String(t.progress ?? 0),
  ]);
  const csv = [header, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── XLSX export (dynamic import to keep bundle lean) ─────────
async function exportXLSX(tasks: Task[], summaryRows: (string | number)[][], periodLabel: string, filename: string) {
  let XLSX: any;
  try {
    XLSX = await import('xlsx');
  } catch {
    alert('Excel export is not available. Please use CSV export instead.');
    return;
  }

  const wb = XLSX.utils.book_new();

  // Sheet 1 – Summary
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['IssueTracker – Task Report'],
    ['Period', periodLabel],
    ['Generated', new Date().toLocaleString()],
    [],
    ...summaryRows,
  ]), 'Summary');

  // Sheet 2 – All Tasks
  const taskHeader = ['Title', 'Category', 'Priority', 'Status', 'Task Date', 'Deadline', 'Carried Over', 'Progress (%)'];
  const taskData   = tasks.map(t => [
    t.title, t.category, t.priority, t.status, t.task_date,
    t.deadline ? new Date(t.deadline).toLocaleString() : '',
    t.carried_over ? 'Yes' : 'No',
    t.progress ?? 0,
  ]);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([taskHeader, ...taskData]), 'All Tasks');

  XLSX.writeFile(wb, `${filename}.xlsx`);
}

// ─── Breakdown helpers ────────────────────────────────────────
const CATEGORIES: TaskCategory[] = ['Development', 'Design', 'Testing', 'Meeting', 'Research', 'General', 'Other'];
const PRIORITIES: Priority[]     = ['High', 'Medium', 'Low'];

interface BreakdownRow {
  label: string;
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
}

function buildDayBreakdown(tasks: Task[], start: Date, end: Date): BreakdownRow[] {
  const days: BreakdownRow[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const dateStr = toYMD(cursor);
    const day     = tasks.filter(t => t.task_date === dateStr);
    days.push({
      label:      dayLabel(dateStr),
      total:      day.length,
      completed:  day.filter(t => t.status === 'Completed').length,
      inProgress: day.filter(t => t.status === 'In Progress').length,
      pending:    day.filter(t => t.status === 'Pending').length,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function buildCategoryBreakdown(tasks: Task[]): BreakdownRow[] {
  return CATEGORIES
    .map(cat => ({
      label:      cat,
      total:      tasks.filter(t => t.category === cat).length,
      completed:  tasks.filter(t => t.category === cat && t.status === 'Completed').length,
      inProgress: tasks.filter(t => t.category === cat && t.status === 'In Progress').length,
      pending:    tasks.filter(t => t.category === cat && t.status === 'Pending').length,
    }))
    .filter(r => r.total > 0);
}

function buildPriorityBreakdown(tasks: Task[]): BreakdownRow[] {
  return PRIORITIES.map(pri => ({
    label:      pri,
    total:      tasks.filter(t => t.priority === pri).length,
    completed:  tasks.filter(t => t.priority === pri && t.status === 'Completed').length,
    inProgress: tasks.filter(t => t.priority === pri && t.status === 'In Progress').length,
    pending:    tasks.filter(t => t.priority === pri && t.status === 'Pending').length,
  })).filter(r => r.total > 0);
}

// ─── Sub-components ───────────────────────────────────────────
function StatCard({ label, value, sub, color }: { label: string; value: number | string; sub?: string; color: string }) {
  return (
    <div className="report-stat-card" style={{ borderTopColor: color }}>
      <div className="report-stat-value" style={{ color }}>{value}</div>
      <div className="report-stat-label">{label}</div>
      {sub && <div className="report-stat-sub">{sub}</div>}
    </div>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="report-table-section">
      <h3 className="report-table-title">{title}</h3>
      <div className="report-table-wrap">
        <table className="report-table">
          <thead>
            <tr>
              <th>{title.includes('Day') ? 'Date' : title.replace(' Breakdown', '')}</th>
              <th>Total</th>
              <th>Completed</th>
              <th>In Progress</th>
              <th>Pending</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const rate = pct(row.completed, row.total);
              return (
                <tr key={row.label} className={row.total === 0 ? 'report-row-empty' : ''}>
                  <td className="report-td-label">{row.label}</td>
                  <td className="report-td-num">{row.total}</td>
                  <td className="report-td-num report-td-completed">{row.completed}</td>
                  <td className="report-td-num report-td-progress">{row.inProgress}</td>
                  <td className="report-td-num report-td-pending">{row.pending}</td>
                  <td className="report-td-rate">
                    <div className="report-rate-bar">
                      <div className="report-rate-fill" style={{ width: `${rate}%` }} />
                    </div>
                    <span className="report-rate-pct">{rate}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────
export default function TaskReports() {
  const { user, role } = useAuth();
  const { showToast }  = useToast();

  type Period = 'week' | 'month';
  const [period,    setPeriod]    = useState<Period>('week');
  const [anchor,    setAnchor]    = useState(new Date());
  const [tasks,     setTasks]     = useState<Task[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);

  const bounds = useMemo(
    () => period === 'week' ? getWeekBounds(anchor) : getMonthBounds(anchor),
    [period, anchor]
  );

  const isCurrentPeriod = useMemo(() => {
    const today = new Date();
    const cur = period === 'week' ? getWeekBounds(today) : getMonthBounds(today);
    return toYMD(bounds.start) === toYMD(cur.start);
  }, [bounds, period]);

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const from = toYMD(bounds.start);
    const to   = toYMD(bounds.end);

    try {
      const data = await api.get<Task[]>(
        `/api/tasks?date_from=${from}&date_to=${to}`
      );

      // Developers see only their assigned tasks; admins see company-wide
      const filtered = role !== 'admin'
        ? data.filter(t => t.assigned_developer_id === user.id)
        : data;

      setTasks(filtered ?? []);
    } catch (err: any) {
      showToast(err.message || 'Failed to load report data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, role, bounds, showToast]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Navigation
  const goPrev = () => {
    const d = new Date(anchor);
    if (period === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setAnchor(d);
  };
  const goNext = () => {
    const d = new Date(anchor);
    if (period === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setAnchor(d);
  };
  const goToday = () => setAnchor(new Date());

  // Summary stats
  const total      = tasks.length;
  const completed  = tasks.filter(t => t.status === 'Completed').length;
  const inProgress = tasks.filter(t => t.status === 'In Progress').length;
  const pending    = tasks.filter(t => t.status === 'Pending').length;
  const rate       = pct(completed, total);
  const carriedCt  = tasks.filter(t => t.carried_over).length;

  // Breakdown tables
  const dayBreakdown  = useMemo(() => buildDayBreakdown(tasks, bounds.start, bounds.end), [tasks, bounds]);
  const catBreakdown  = useMemo(() => buildCategoryBreakdown(tasks), [tasks]);
  const priBreakdown  = useMemo(() => buildPriorityBreakdown(tasks), [tasks]);

  const periodLabel = fmtPeriod(bounds.start, bounds.end);
  const filename    = `task-report-${period}-${toYMD(bounds.start)}`;

  // Summary rows for Excel export
  const summaryRows: (string | number)[][] = [
    ['Metric', 'Value'],
    ['Period', periodLabel],
    ['Total Tasks', total],
    ['Completed', completed],
    ['In Progress', inProgress],
    ['Pending', pending],
    ['Completion Rate', `${rate}%`],
    ['Carried Over', carriedCt],
  ];

  const handleExportCSV = () => {
    if (tasks.length === 0) { showToast('No tasks to export.', 'error'); return; }
    exportCSV(tasks, filename);
    showToast('CSV downloaded.', 'success');
  };

  const handleExportXLSX = async () => {
    if (tasks.length === 0) { showToast('No tasks to export.', 'error'); return; }
    setExporting(true);
    await exportXLSX(tasks, summaryRows, periodLabel, filename);
    setExporting(false);
    showToast('Excel file downloaded.', 'success');
  };

  return (
    <div className="page reports-page">
      {/* Header */}
      <div className="report-header">
        <div className="report-header-left">
          <Link to="/tasks" className="back-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Tasks
          </Link>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Work Reports</h1>
            <p className="page-subtitle">
              {role === 'admin' ? 'Company-wide task breakdown' : 'Your personal task breakdown'}
            </p>
          </div>
        </div>

        <div className="report-header-actions">
          <button className="btn btn-secondary" onClick={handleExportCSV} title="Export as CSV">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            CSV
          </button>
          <button className="btn btn-secondary" onClick={handleExportXLSX} disabled={exporting} title="Export as Excel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8l5 5 5-5M7 16l5-5 5 5"/>
            </svg>
            {exporting ? 'Exporting…' : 'Excel'}
          </button>
        </div>
      </div>

      {/* Period controls */}
      <div className="report-controls">
        {/* Period tabs */}
        <div className="report-period-tabs">
          <button className={`report-period-tab${period === 'week' ? ' active' : ''}`} onClick={() => { setPeriod('week'); setAnchor(new Date()); }}>
            Weekly
          </button>
          <button className={`report-period-tab${period === 'month' ? ' active' : ''}`} onClick={() => { setPeriod('month'); setAnchor(new Date()); }}>
            Monthly
          </button>
        </div>

        {/* Navigation */}
        <div className="report-nav">
          <button className="report-nav-btn" onClick={goPrev} title="Previous period">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <span className="report-period-label">{periodLabel}</span>
          <button
            className="report-nav-btn"
            onClick={goNext}
            disabled={isCurrentPeriod}
            title="Next period"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          {!isCurrentPeriod && (
            <button className="btn btn-secondary btn-sm" onClick={goToday} style={{ marginLeft: 6 }}>
              Current {period === 'week' ? 'Week' : 'Month'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-grid">
          {[1, 2, 3, 4].map(i => <div key={i} className="issue-card-skeleton" style={{ height: 100 }} />)}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="report-stats-grid">
            <StatCard label="Total Tasks"     value={total}      color="#6366f1" />
            <StatCard label="Completed"       value={completed}  color="#16a34a" sub={`${rate}% completion rate`} />
            <StatCard label="In Progress"     value={inProgress} color="#2563eb" />
            <StatCard label="Pending"         value={pending}    color="#d97706" />
            {carriedCt > 0 && (
              <StatCard label="Carried Over"  value={carriedCt}  color="#9333ea" sub="from previous days" />
            )}
          </div>

          {/* Completion progress bar */}
          {total > 0 && (
            <div className="report-progress-section">
              <div className="report-progress-header">
                <span>Overall completion</span>
                <strong>{rate}%</strong>
              </div>
              <div className="report-progress-bar">
                <div className="report-progress-fill" style={{ width: `${rate}%` }} />
              </div>
            </div>
          )}

          {total === 0 ? (
            <div className="report-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="48" height="48" style={{ color: 'var(--color-text-muted)' }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <p>No tasks found for this period.</p>
            </div>
          ) : (
            <>
              <BreakdownTable title="Daily Breakdown" rows={dayBreakdown} />
              <BreakdownTable title="Category Breakdown" rows={catBreakdown} />
              <BreakdownTable title="Priority Breakdown" rows={priBreakdown} />

              {/* Task list */}
              <div className="report-table-section">
                <h3 className="report-table-title">All Tasks ({total})</h3>
                <div className="report-table-wrap">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Deadline</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map(t => (
                        <tr key={t.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {t.carried_over && <span className="report-carried-chip" title="Carried over">↩</span>}
                              <span>{t.title}</span>
                            </div>
                          </td>
                          <td><span className="report-cat-chip">{t.category}</span></td>
                          <td>
                            <span className={`priority-badge priority-${t.priority.toLowerCase()}`}>{t.priority}</span>
                          </td>
                          <td>
                            <span className={`status-badge status-${t.status === 'Completed' ? 'closed' : t.status === 'In Progress' ? 'in-progress' : 'open'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t.task_date}</td>
                          <td style={{ color: t.deadline ? '#dc2626' : 'var(--color-text-muted)', fontSize: 13 }}>
                            {t.deadline ? new Date(t.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
